import { useState, useEffect, useRef } from 'react';
import { DeploymentTable } from './components/DeploymentTable';
import { ChatBot } from './components/ChatBot';
import { HistoryView } from './components/HistoryView';
import { HelpView } from './components/HelpView';
import { ECSJobsView } from './components/ECSJobsView';
import { HealthCheckSettings } from './components/HealthCheckSettings';
import { fetchDeployments, fetchConfig, checkCodeDeployStatus, checkCodeDeployJobDetails, checkEcsDeployStatus, checkEcsServiceDetails, saveAppHealthcheckMappings, checkHealthStatus } from './api';
import { Deployment, Config, AppHealthcheckMapping, HealthcheckType } from './types';
import { getEnvColor, getEnvTextColor } from './utils';
import logoAnimated from '../../design/radar-logo-animated.svg';
import logoStatic from '../../design/radar-logo-static.svg';
import chatIcon from './assets/icons/bender2.png';

function App() {
  const [deployments, setDeployments] = useState<Deployment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [helpView, setHelpView] = useState(false);
  const [selectedDeployment, setSelectedDeployment] = useState<Deployment | null>(null);
  const [historyDeployment, setHistoryDeployment] = useState<Deployment | null>(null);
  const [config, setConfig] = useState<Config | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [refreshingCodeDeploy, setRefreshingCodeDeploy] = useState(false);
  const [refreshingEcsDeploy, setRefreshingEcsDeploy] = useState(false);
  const [refreshingHealthCheck, setRefreshingHealthCheck] = useState(false);
  const [statusDetailOpen, setStatusDetailOpen] = useState(false);
  const [statusDetailLoading, setStatusDetailLoading] = useState(false);
  const [statusDetailError, setStatusDetailError] = useState<string | null>(null);
  const [statusDetailData, setStatusDetailData] = useState<any>(null);
  const [statusDetailType, setStatusDetailType] = useState<'codedeploy' | 'ecs' | null>(null);
  const [currentPage, setCurrentPage] = useState<'deployments' | 'ecs-jobs'>('deployments');
  const [menuOpen, setMenuOpen] = useState(false);
  const [modalView, setModalView] = useState<'details' | 'settings'>('details');
  const [isLogoAnimated, setIsLogoAnimated] = useState(true);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };

    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuOpen]);

  useEffect(() => {
    loadDeployments();
    loadConfig();
  }, []);

  const loadDeployments = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await fetchDeployments();
      setDeployments(data);
      setLastUpdated(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load deployments');
    } finally {
      setLoading(false);
    }
  };

  const loadConfig = async () => {
    try {
      const data = await fetchConfig();
      console.log('Config loaded:', data);
      setConfig(data);
    } catch (err) {
      console.error('Failed to load config:', err);
    }
  };

  const handleRowClick = (deployment: Deployment) => {
    setSelectedDeployment(deployment);
    setModalView('details');
  };

  const closeModal = () => {
    setSelectedDeployment(null);
  };

  const openHistory = (deployment: Deployment) => {
    setSelectedDeployment(null);
    setHistoryDeployment(deployment);
  };

  const closeHistory = () => {
    setHistoryDeployment(null);
  };

  const handleSaveHealthCheck = async (mapping: AppHealthcheckMapping) => {
    if (!config) return;

    // Find and update the mapping in the array, or add if not exists
    const existingIndex = config.appHealthcheckMappings?.findIndex(
      (m) => m.Env === mapping.Env && m.App === mapping.App
    ) ?? -1;

    let updatedMappings: AppHealthcheckMapping[];
    if (existingIndex >= 0) {
      updatedMappings = [...config.appHealthcheckMappings];
      updatedMappings[existingIndex] = mapping;
    } else {
      updatedMappings = [...(config.appHealthcheckMappings || []), mapping];
    }

    // Save to backend
    await saveAppHealthcheckMappings(updatedMappings);

    // Update local config state
    setConfig({
      ...config,
      appHealthcheckMappings: updatedMappings,
    });
  };

  const handleRefreshCodeDeploy = async () => {
    if (!selectedDeployment?.CodeDeployId) return;

    setRefreshingCodeDeploy(true);
    try {
      const result = await checkCodeDeployStatus(
        selectedDeployment.Env,
        selectedDeployment.App
      );

      // Update selectedDeployment
      const updatedDeployment = {
        ...selectedDeployment,
        DeploymentStatus: result.deploymentStatus,
      };
      setSelectedDeployment(updatedDeployment);

      // Update deployments array
      setDeployments((prev) =>
        prev.map((d) =>
          d.Env === selectedDeployment.Env && d.App === selectedDeployment.App
            ? { ...d, DeploymentStatus: result.deploymentStatus }
            : d
        )
      );
    } catch (err) {
      console.error('Failed to refresh CodeDeploy status:', err);
    } finally {
      setRefreshingCodeDeploy(false);
    }
  };

  const handleRefreshEcsDeploy = async () => {
    if (!selectedDeployment?.ECSCluster || !selectedDeployment?.ECSService) return;

    setRefreshingEcsDeploy(true);
    try {
      const result = await checkEcsDeployStatus(
        selectedDeployment.Env,
        selectedDeployment.App
      );

      // Update selectedDeployment
      const updatedDeployment = {
        ...selectedDeployment,
        DeploymentStatus: result.deploymentStatus,
      };
      setSelectedDeployment(updatedDeployment);

      // Update deployments array
      setDeployments((prev) =>
        prev.map((d) =>
          d.Env === selectedDeployment.Env && d.App === selectedDeployment.App
            ? { ...d, DeploymentStatus: result.deploymentStatus }
            : d
        )
      );
    } catch (err) {
      console.error('Failed to refresh ECS deploy status:', err);
    } finally {
      setRefreshingEcsDeploy(false);
    }
  };

  const getSelectedHealthcheckType = (): HealthcheckType | null => {
    if (!selectedDeployment || !config) return null;
    const mapping = config.appHealthcheckMappings.find(
      m => m.Env === selectedDeployment.Env && m.App === selectedDeployment.App
    );
    return mapping?.healthcheck_type || null;
  };

  const handleRefreshHealthCheck = async () => {
    if (!selectedDeployment) return;

    setRefreshingHealthCheck(true);
    try {
      const result = await checkHealthStatus(
        selectedDeployment.Env,
        selectedDeployment.App
      );

      // Only update if we got a health status back
      if (result.healthStatus) {
        // Update selectedDeployment (modal)
        const updatedDeployment = {
          ...selectedDeployment,
          HealthStatus: result.healthStatus,
          HealthCheckTime: result.checkTime,
        };
        setSelectedDeployment(updatedDeployment);

        // Update deployments array (table)
        setDeployments((prev) =>
          prev.map((d) =>
            d.Env === selectedDeployment.Env && d.App === selectedDeployment.App
              ? { ...d, HealthStatus: result.healthStatus ?? undefined, HealthCheckTime: result.checkTime }
              : d
          )
        );
      }
    } catch (err) {
      console.error('Failed to refresh health check:', err);
    } finally {
      setRefreshingHealthCheck(false);
    }
  };

  const handleCodeDeployLinkClick = async () => {
    if (!selectedDeployment?.CodeDeployId || !selectedDeployment?.Account) return;
    setStatusDetailType('codedeploy');
    setStatusDetailOpen(true);
    setStatusDetailLoading(true);
    setStatusDetailError(null);
    setStatusDetailData(null);
    try {
      const result = await checkCodeDeployJobDetails(
        selectedDeployment.CodeDeployId,
        selectedDeployment.Account
      );
      setStatusDetailData(result);
    } catch (err) {
      setStatusDetailError(err instanceof Error ? err.message : 'Failed to fetch details');
    } finally {
      setStatusDetailLoading(false);
    }
  };

  const handleEcsLinkClick = async () => {
    if (!selectedDeployment?.ECSCluster) return;
    setStatusDetailType('ecs');
    setStatusDetailOpen(true);
    setStatusDetailLoading(true);
    setStatusDetailError(null);
    setStatusDetailData(null);
    try {
      const result = await checkEcsServiceDetails(selectedDeployment.Env, selectedDeployment.App);
      setStatusDetailData(result);
    } catch (err) {
      setStatusDetailError(err instanceof Error ? err.message : 'Failed to fetch details');
    } finally {
      setStatusDetailLoading(false);
    }
  };

  const closeStatusDetail = () => {
    setStatusDetailOpen(false);
    setStatusDetailData(null);
    setStatusDetailType(null);
    setStatusDetailError(null);
  };

  const handlePageChange = (page: 'deployments' | 'ecs-jobs') => {
    setCurrentPage(page);
    setMenuOpen(false);
    // Reset other view states when changing pages
    setHelpView(false);
    setHistoryDeployment(null);
  };

  const getPageTitle = () => {
    return currentPage === 'deployments' ? 'Deployments' : 'Jobs';
  };

  return (
    <div className="app">
      {/* Header */}
      <header className="app-header">
        <div className="header-left">
          <img
            src={isLogoAnimated ? logoAnimated : logoStatic}
            alt="Logo"
            className="header-logo"
            onClick={() => setIsLogoAnimated(!isLogoAnimated)}
            style={{ cursor: 'pointer' }}
          />
          <div className="nav-menu-container" ref={menuRef}>
            <button
              className="nav-menu-trigger"
              onClick={() => setMenuOpen(!menuOpen)}
            >
              EZ Mission Control
              <span className="nav-arrow">{menuOpen ? '▲' : '▼'}</span>
            </button>
            {menuOpen && (
              <div className="nav-dropdown">
                <button
                  className={`nav-dropdown-item ${currentPage === 'deployments' ? 'active' : ''}`}
                  onClick={() => handlePageChange('deployments')}
                >
                  Deployments
                </button>
                <button
                  className={`nav-dropdown-item ${currentPage === 'ecs-jobs' ? 'active' : ''}`}
                  onClick={() => handlePageChange('ecs-jobs')}
                >
                  Jobs
                </button>
              </div>
            )}
          </div>
        </div>
        <h1 className="page-title">{getPageTitle()}</h1>
        <div className="header-actions">
          {lastUpdated && (
            <span className="last-updated">
              Last updated: {lastUpdated.toLocaleTimeString()}
            </span>
          )}
          <button className="refresh-btn" onClick={loadDeployments} disabled={loading}>
            {loading ? 'Loading...' : 'Refresh'}
          </button>
          <button
            className="chat-toggle-btn"
            onClick={() => setChatOpen(true)}
            title="Ask Assistant"
          >
            <img src={chatIcon} alt="Chat" className="chat-icon" />
          </button>
          <button
            className="help-btn"
            onClick={() => setHelpView(true)}
            title="Help"
          >
            ?
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="app-main">
        {currentPage === 'ecs-jobs' ? (
          <ECSJobsView />
        ) : helpView ? (
          <HelpView onBack={() => setHelpView(false)} />
        ) : historyDeployment ? (
          <HistoryView
            env={historyDeployment.Env}
            app={historyDeployment.App}
            onBack={closeHistory}
          />
        ) : (
          <DeploymentTable
            deployments={deployments}
            loading={loading}
            error={error}
            onRowClick={handleRowClick}
          />
        )}
      </main>

      {/* Deployment Detail Modal */}
      {selectedDeployment && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div
              className="modal-header"
              style={{
                backgroundColor: getEnvColor(selectedDeployment.Env),
                color: getEnvTextColor(selectedDeployment.Env)
              }}
            >
              <div className="modal-title">
                <h2>{selectedDeployment.Env} {selectedDeployment.App}</h2>
              </div>
              <div className="modal-view-toggles">
                <button
                  className={`modal-view-btn ${modalView === 'details' ? 'active' : ''}`}
                  onClick={() => setModalView('details')}
                  title="Details"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="4" cy="6" r="1.5" fill="currentColor" stroke="none" />
                    <line x1="9" y1="6" x2="20" y2="6" />
                    <circle cx="4" cy="12" r="1.5" fill="currentColor" stroke="none" />
                    <line x1="9" y1="12" x2="20" y2="12" />
                    <circle cx="4" cy="18" r="1.5" fill="currentColor" stroke="none" />
                    <line x1="9" y1="18" x2="20" y2="18" />
                  </svg>
                </button>
                <button
                  className={`modal-view-btn ${modalView === 'settings' ? 'active' : ''}`}
                  onClick={() => setModalView('settings')}
                  title="Settings"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="3" />
                    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
                  </svg>
                </button>
                <button
                  className="modal-view-btn"
                  onClick={() => openHistory(selectedDeployment)}
                  title="History"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M12 6v6l4 2" />
                  </svg>
                </button>
              </div>
              <button className="close-btn" onClick={closeModal}>×</button>
            </div>
            <div className="modal-body">
              {modalView === 'details' ? (
                <>
                  <div className="detail-row">
                    <span className="label">Account:</span>
                    <span className="value">{selectedDeployment.Account || ''}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Version:</span>
                    <span className="value">{selectedDeployment.Ver}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Deployed At:</span>
                    <span className="value">{new Date(selectedDeployment.DeployAt).toLocaleString()}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Deployed By:</span>
                    <span className="value">{selectedDeployment.DeployFor}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">App Type:</span>
                    <span className="value">{config?.appTypeMappings?.[selectedDeployment.App] || 'Unknown'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Deployment Status:</span>
                    <span className="value">{selectedDeployment.DeploymentStatus || 'Unknown'}</span>
                  </div>
                  {(selectedDeployment.ECSCluster || selectedDeployment.ECSService) && (
                    <div className="detail-row">
                      <span className="label">ECS info:</span>
                      <span className="value codedeploy-value">
                        <button className="status-link" onClick={handleEcsLinkClick} title="View ECS service details">
                          {selectedDeployment.ECSCluster}.{selectedDeployment.ECSService}
                        </button>
                        <button
                          className="refresh-icon-btn"
                          onClick={handleRefreshEcsDeploy}
                          disabled={refreshingEcsDeploy}
                          title="Refresh ECS deployment status"
                        >
                          {refreshingEcsDeploy ? (
                            <span className="spinner-small" />
                          ) : (
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M23 4v6h-6M1 20v-6h6M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                            </svg>
                          )}
                        </button>
                      </span>
                    </div>
                  )}
                  {selectedDeployment.CodeDeployId && (
                    <div className="detail-row">
                      <span className="label">CodeDeploy ID:</span>
                      <span className="value codedeploy-value">
                        <button className="status-link" onClick={handleCodeDeployLinkClick} title="View CodeDeploy details">
                          {selectedDeployment.CodeDeployId}
                        </button>
                        <button
                          className="refresh-icon-btn"
                          onClick={handleRefreshCodeDeploy}
                          disabled={refreshingCodeDeploy}
                          title="Refresh CodeDeploy status"
                        >
                          {refreshingCodeDeploy ? (
                            <span className="spinner-small" />
                          ) : (
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M23 4v6h-6M1 20v-6h6M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                            </svg>
                          )}
                        </button>
                      </span>
                    </div>
                  )}
                  <div className="detail-row">
                    <span className="label">Health Status:</span>
                    <span className="value health-status-value">
                      {selectedDeployment.HealthStatus || 'Unknown'}
                      {getSelectedHealthcheckType() && getSelectedHealthcheckType() !== 'None' && (
                        <button
                          className="refresh-icon-btn"
                          onClick={handleRefreshHealthCheck}
                          disabled={refreshingHealthCheck}
                          title="Refresh health status"
                        >
                          {refreshingHealthCheck ? (
                            <span className="spinner-small" />
                          ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.3"/>
                            </svg>
                          )}
                        </button>
                      )}
                    </span>
                  </div>
                  {selectedDeployment.Previous && (
                    <div className="detail-row">
                      <span className="label">Previous:</span>
                      <span
                        className="value previous"
                        dangerouslySetInnerHTML={{
                          __html: selectedDeployment.Previous.replace(/<br\s*\/?>/gi, '<br/>'),
                        }}
                      />
                    </div>
                  )}
                  {selectedDeployment.Notes && selectedDeployment.Notes !== '" "' && (
                    <div className="detail-row">
                      <span className="label">Notes:</span>
                      <span
                        className="value notes"
                        dangerouslySetInnerHTML={{
                          __html: selectedDeployment.Notes.replace(/<br\s*\/?>/gi, '<br/>'),
                        }}
                      />
                    </div>
                  )}
                </>
              ) : (
                <div className="modal-settings">
                  <HealthCheckSettings
                    deployment={selectedDeployment}
                    config={config}
                    onSave={handleSaveHealthCheck}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Status Detail Modal */}
      {statusDetailOpen && (
        <div className="modal-overlay status-detail-overlay" onClick={closeStatusDetail}>
          <div className="modal-content status-detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{statusDetailType === 'codedeploy' ? 'CodeDeploy Details' : 'ECS Service Details'}</h2>
              <button className="close-btn" onClick={closeStatusDetail}>×</button>
            </div>
            <div className="status-detail-body">
              {statusDetailLoading && (
                <div className="status-loading">
                  <span className="spinner" />
                  <span>Loading...</span>
                </div>
              )}
              {statusDetailError && (
                <div className="status-error">Error: {statusDetailError}</div>
              )}
              {!statusDetailLoading && !statusDetailError && statusDetailData && (
                <>
                  {statusDetailData.summary && (
                    <div className="status-summary">
                      <strong>Summary:</strong> {statusDetailData.summary}
                    </div>
                  )}
                  {statusDetailData.consoleUrl && (
                    <a
                      href={statusDetailData.consoleUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="console-link"
                    >
                      Open in AWS Console
                    </a>
                  )}
                  <pre className="status-json">{JSON.stringify(statusDetailData, null, 2)}</pre>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ChatBot */}
      <ChatBot isOpen={chatOpen} onClose={() => setChatOpen(false)} />



      {/* Footer */}
      <footer className="app-footer">
        <p>EZ Mission Control</p>
      </footer>
    </div>
  );
}

export default App;
